import useAllocations from '@hooks/queries/registry/tokenStandard/useAllocations';
import { useTransferOffers } from '@hooks/queries/registry/transferInstruction/useTransferOffers';
import useHoldings from '@hooks/queries/registry/useHoldings';
import React from 'react';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { filterHoldingsByInstrument } from '../../utils/filterHoldingsByInstrument';
import { filterHoldingsByOwner } from '../../utils/filterHoldingsByOwner';
import { MAX_CONTRACT_LIMIT } from './useGetPrepareHoldings';

type Args = {
  instrumentId: string;
  registrar: string;
  owner: string;
};

export const useGetPrepareHoldingsWithLocks = () => {
  const holdings = useHoldings();
  const transferOffers = useTransferOffers();
  const allocations = useAllocations();

  return React.useCallback(
    (args: Args) => {
      const { instrumentId, registrar, owner } = args;

      const instrumentHoldings = filterHoldingsByInstrument({
        instrumentId,
        registrar,
        holdings: holdings.data || [],
      });

      const ownedHoldings = filterHoldingsByOwner({
        owner,
        holdings: instrumentHoldings,
      });

      const holdingCids = ownedHoldings.map((h) => h.contractId as string);

      const relevantTransferOffers = (transferOffers.data || []).filter(
        (t) =>
          t.payload.transfer.instrumentId.id === instrumentId &&
          t.payload.transfer.instrumentId.admin === registrar &&
          t.payload.transfer.sender === owner,
      );

      const transferOfferCids = relevantTransferOffers.map((t) => t.contractId as string);

      const relevantAllocations = (allocations.data || []).filter((a) => {
        const view = a.view(Allocation);
        return (
          view.allocation.transferLeg.instrumentId.id === instrumentId &&
          view.allocation.transferLeg.instrumentId.admin === registrar &&
          view.allocation.transferLeg.sender === owner
        );
      });

      const allocationCids = relevantAllocations.map((a) => a.contractId as string);

      const totalContracts = holdingCids.length + transferOfferCids.length + allocationCids.length;
      const isLoading = holdings.isLoading || transferOffers.isLoading || allocations.isLoading;

      return {
        holdingCids: isLoading ? [] : holdingCids,
        transferOfferCids: isLoading ? [] : transferOfferCids,
        allocationCids: isLoading ? [] : allocationCids,
        exceedsMaxLimit: !isLoading && totalContracts > MAX_CONTRACT_LIMIT,
      };
    },
    [
      holdings.data,
      holdings.isLoading,
      transferOffers.data,
      transferOffers.isLoading,
      allocations.data,
      allocations.isLoading,
    ],
  );
};
